import {
  Avatar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useChannelStateContext } from "stream-chat-react";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { ChatRoleBadge } from "Components/Chat/ChatRoleBadge";
import {
  getChatMemberProfileUrl,
  identityUserPublicIdFromStreamId,
} from "Components/Chat/chatConstants";
import { Link } from "Navigation/Link";
import { useRemoveChatChannelMemberMutation } from "Api/Mutations/Chat/useRemoveChatChannelMemberMutation";
import { useDisclosedNamesMapQuery } from "Api/Queries/Social/useDisclosedNamesMapQuery";
import { useDisclosedNameResolver } from "Hooks/Chat/useDisclosedNameResolver";
import { resolveMemberNames } from "Utils/Chat/resolveMemberNames";

const RESOURCES = Resources.Chat.Members;
const COMMON_RESOURCES = Resources.Common;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  isCreator: boolean;
};

export const ChatMemberList = ({ isOpen, onClose, isCreator }: Props) => {
  const { channel, members } = useChannelStateContext();
  const { t } = useResource();

  const currentUserId = channel.state.membership?.user?.id;
  const { data: disclosedNames } = useDisclosedNamesMapQuery();
  // The list resolves every OTHER member; the viewer's own row needs the self case the resolver
  // holds (their own name is not in the disclosed list, and Stream carries their @handle).
  const resolveName = useDisclosedNameResolver();
  const channelOwnerId = channel.data?.created_by?.id;
  // A frozen channel is anonymised (ADR-0008): its members must stay unidentifiable, so no
  // profile links — not even dead ones.
  const isAnonymized = channel.data?.frozen === true;
  const membersArray = resolveMemberNames(
    Object.values(members ?? {}),
    disclosedNames,
    {
      isAnonymized,
      anonymousName: t(COMMON_RESOURCES.AnonymousUser),
    },
  );

  const { mutate: removeMember } = useRemoveChatChannelMemberMutation({
    onError: () => toast.error(t(RESOURCES.RemoveError)),
  });

  const handleRemoveMember = (streamUserId: string) => {
    removeMember({
      channelId: channel.id ?? "",
      memberIdentityUserPublicID:
        identityUserPublicIdFromStreamId(streamUserId),
    });
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 300, maxHeight: "100%" } } }}
    >
      <Box p={2} pb={1}>
        <Typography fontWeight={600}>
          {t(RESOURCES.Title)} ({membersArray.length})
        </Typography>
      </Box>
      <List dense>
        {membersArray.map(({ user_id, user }) => {
          const isCurrentUser = user_id === currentUserId;
          const isOwner = user_id === channelOwnerId;
          const name =
            (isCurrentUser && !isAnonymized
              ? resolveName(user_id, user?.name)
              : user?.name) || t(COMMON_RESOURCES.AnonymousUser);
          const isOnline = !!user?.online;
          // Every other member leads to their public profile, as the 1:1 channel header does.
          const profileUrl =
            !isCurrentUser && !isAnonymized && user_id
              ? getChatMemberProfileUrl(user_id)
              : undefined;

          const content = (
            <>
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <Box position="relative" display="inline-flex">
                  <Avatar
                    src={user?.image}
                    alt={name}
                    sx={{ width: 28, height: 28 }}
                  />
                  <ChatRoleBadge
                    accessRole={user?.accessRole}
                    avatarSize={28}
                  />
                  {isOnline && (
                    <Box
                      position="absolute"
                      bottom={-1}
                      right={-1}
                      width={10}
                      height={10}
                      borderRadius="50%"
                      border="2px solid"
                      borderColor="background.paper"
                      bgcolor="success.main"
                    />
                  )}
                </Box>
              </ListItemAvatar>
              <ListItemText
                primary={isCurrentUser ? `${name} (${t(RESOURCES.You)})` : name}
                secondary={isOwner ? t(RESOURCES.Owner) : undefined}
              />
            </>
          );

          return (
            <ListItem
              key={user_id}
              disablePadding={!!profileUrl}
              secondaryAction={
                isCreator && !isCurrentUser ? (
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handleRemoveMember(user_id ?? "")}
                    aria-label={t(RESOURCES.Remove)}
                  >
                    <RemoveCircleOutlineIcon fontSize="small" color="error" />
                  </IconButton>
                ) : undefined
              }
            >
              {profileUrl ? (
                <ListItemButton
                  component={Link}
                  to={profileUrl}
                  onClick={onClose}
                >
                  {content}
                </ListItemButton>
              ) : (
                content
              )}
            </ListItem>
          );
        })}
      </List>
    </Drawer>
  );
};
